import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/clinic";
import { Logo } from "@/components/Logo";
import { Eye, EyeOff, ExternalLink, Loader2, Lock, ShieldCheck } from "lucide-react";

export default function Login() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [ricorda, setRicorda] = useState(false);
  const [mostra, setMostra] = useState(false);
  const [errore, setErrore] = useState(
    params.get("scaduta")
      ? "Sessione terminata. Inserisci di nuovo la password."
      : params.get("cambiata")
      ? "Password aggiornata. Accedi con la nuova password."
      : ""
  );
  const [attesa, setAttesa] = useState(false);
  const [cookieBloccato, setCookieBloccato] = useState(false);

  const accedi = async (e) => {
    e.preventDefault();
    if (!password) return;
    setAttesa(true);
    setErrore("");
    setCookieBloccato(false);
    try {
      await api.post("/auth/login", { password, ricorda });
      // Se il browser scarta il cookie (anteprima in iframe, cookie di terze parti
      // bloccati) il login riesce ma la sessione non esiste: lo diciamo chiaramente.
      const { data } = await api.get("/auth/me");
      if (data?.autenticato) {
        window.location.replace("/");
        return;
      }
      setCookieBloccato(true);
      setPassword("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrore(typeof detail === "string" ? detail : "Credenziali non valide");
      setPassword("");
    } finally {
      setAttesa(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1D] text-white relative grain overflow-hidden flex items-center justify-center px-5 py-12">
      <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-violet-500/10 blur-[130px] pointer-events-none" />
      <div className="absolute bottom-[-10rem] right-[-6rem] w-[460px] h-[460px] rounded-full bg-cyan-500/10 blur-[130px] pointer-events-none" />

      <div className="relative w-full max-w-[430px] rise-in">
        <div className="flex items-center gap-4 mb-9">
          <Logo invert className="h-9" />
          <div className="h-9 w-px bg-white/15" />
          <div>
            <div className="font-display text-xl font-bold uppercase tracking-[0.16em] leading-none">
              Gestione Ambulatorio
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mt-1.5">
              Medicina del lavoro
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-7 sm:p-8 shadow-2xl shadow-black/40">
          <div className="flex items-center gap-2.5 text-emerald-300">
            <ShieldCheck className="w-4 h-4" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">Area riservata</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-3">Accesso al gestionale</h1>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">
            I dati dei pazienti sono protetti. Inserisci la password dello studio per continuare.
          </p>

          <form onSubmit={accedi} className="mt-7 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[11px] uppercase tracking-widest text-slate-400">
                Password
              </Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  id="password"
                  type={mostra ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  autoComplete="current-password"
                  placeholder="••••••••••"
                  data-testid="login-password-input"
                  className="pl-10 pr-11 h-12 bg-white/5 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-emerald-400/60"
                />
                <button
                  type="button"
                  onClick={() => setMostra((v) => !v)}
                  data-testid="login-toggle-visibility-btn"
                  title={mostra ? "Nascondi password" : "Mostra password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition-colors"
                >
                  {mostra ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3.5 cursor-pointer hover:border-white/20 transition-colors">
              <Checkbox
                checked={ricorda}
                onCheckedChange={(v) => setRicorda(!!v)}
                data-testid="login-remember-checkbox"
                className="mt-0.5 border-white/30 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
              />
              <span>
                <span className="block text-sm font-medium text-slate-100">Ricorda l'accesso</span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  Resta collegato 30 giorni su questo dispositivo. Senza spunta l'accesso si chiude
                  quando chiudi il browser.
                </span>
              </span>
            </label>

            {cookieBloccato && (
              <div
                data-testid="login-cookie-blocked"
                className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3.5 text-sm text-amber-100 space-y-3"
              >
                <div>
                  Password corretta, ma questo browser ha bloccato il cookie di sessione perché
                  l'app è aperta all'interno di un'altra pagina. Aprila in una finestra dedicata.
                </div>
                <Button
                  type="button"
                  onClick={() => window.open(window.location.origin + "/login", "_blank", "noopener")}
                  data-testid="login-open-new-window-btn"
                  className="w-full rounded-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold"
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Apri in una nuova finestra
                </Button>
              </div>
            )}

            {errore && (
              <div
                data-testid="login-error-message"
                className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
              >
                {errore}
              </div>
            )}

            <Button
              type="submit"
              disabled={attesa || !password}
              data-testid="login-submit-btn"
              className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-base"
            >
              {attesa ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Entra
            </Button>
          </form>
        </div>

        <div className="mt-6 text-center text-[10px] uppercase tracking-[0.25em] text-slate-600">
          Accesso consentito al solo personale autorizzato
        </div>
      </div>
    </div>
  );
}
